// Lógica de armado de datos para "Comparación consumo total", "Intensidad
// industrial" e "Intensidad doméstica" de WaterKPIsDashBoard.js, extraída a
// un módulo aparte (sin JSX/hooks) para poder reusarla también desde el
// export a PDF (pdfWaterCharts.js) sin duplicar la lógica ni crear un
// import circular con WaterKPIsDashBoard.js. Mismo patrón que
// EnergyKPIsDashboard/energyChartData.js.

import { convertUnitsFinal } from "../../../../utils/dataAndFormulas/waterForms.js";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function buildYearColorMap(years) {
  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const chartColors = {
    oldest: getCssVar("--chart-year-oldest"),
    prev: getCssVar("--chart-year-prev"),
    last: getCssVar("--chart-year-last"),
  };
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: chartColors.oldest || "#cbd5e1",
    [sorted[1]]: chartColors.prev || "#94a3b8",
    [sorted[2]]: chartColors.last || "#1e293b",
  };
}

export function buildSharedYScale(...dataArrays) {
  const combinedMax = Math.max(
    0,
    ...dataArrays.flatMap((arr) => arr.map((d) => d.value || 0)),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  return { domainMin: 0, domainMax: yDomainMax, nice: true };
}

function last3Years(resultsByYear) {
  return Object.keys(resultsByYear || {})
    .map(Number)
    .sort((a, b) => a - b)
    .slice(-3)
    .map(String);
}

// "Comparación consumo total" — misma lógica que usaba el useMemo de
// WaterComparisonChart.
export function buildWaterComparisonData(resultsByYear, t) {
  const years = last3Years(resultsByYear);
  const bar = [];
  const line = [];

  years.forEach((year) => {
    const values = months.map(
      (month) => resultsByYear?.[year]?.[month]?.total_water_month?.total || 0,
    );

    const monthsWithData = values.filter((v) => v > 0);
    const currentYear = new Date().getFullYear().toString();
    const divisor = year === currentYear ? (monthsWithData.length || 1) : 12;
    const totalSum = values.reduce((a, b) => a + b, 0);
    const avg = totalSum / divisor;

    months.forEach((month, i) => {
      const total = values[i];
      if (total > 0) {
        bar.push({ year: String(year), month: t(month), value: total, type: "Consumo" });
      }
      line.push({ year: String(year), month: t(month), value: avg, type: "Promedio" });
    });
  });

  const sortData = (arr) => arr.sort((a, b) => {
    if (a.year !== b.year) return Number(b.year) - Number(a.year);
    if (a.type === "Promedio" && b.type === "Consumo") return -1;
    if (a.type === "Consumo" && b.type === "Promedio") return 1;
    return 0;
  });

  return {
    years,
    barData: sortData(bar),
    lineData: sortData(line),
    yearColorMap: buildYearColorMap(years),
  };
}

function sumWaterProductionSources(monthData) {
  return (
    convertUnitsFinal(monthData.municipal_network_water_production?.total, monthData.municipal_network_water_production?.unit, "l") +
    convertUnitsFinal(monthData.groundwater_production?.total, monthData.groundwater_production?.unit, "l") +
    convertUnitsFinal(monthData.surface_water?.total, monthData.surface_water?.unit, "l") +
    convertUnitsFinal(monthData.distilled_water?.total, monthData.distilled_water?.unit, "l") +
    convertUnitsFinal(monthData.reused_water?.total, monthData.reused_water?.unit, "l") +
    convertUnitsFinal(monthData.recycled_water?.total, monthData.recycled_water?.unit, "l") +
    convertUnitsFinal(monthData.steam_condensed_water?.total, monthData.steam_condensed_water?.unit, "l")
  );
}

// "Intensidad industrial" — misma lógica que usaba el useMemo de
// WaterProductionIntensityChart.
export function buildWaterProductionIntensityData(resultsByYear, resultsProductions, t) {
  const years = last3Years(resultsByYear);
  const cData = [];
  const aData = [];

  years.forEach((year) => {
    const totals = months.map((m) => {
      const monthData = resultsByYear?.[year]?.[m] || {};
      const energy = sumWaterProductionSources(monthData);
      const production = resultsProductions?.[year]?.[m] || 0;
      return (production > 0 && energy > 0) ? (energy / production) : null;
    });

    const validValues = totals.filter((v) => v !== null);
    const avg = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : null;

    months.forEach((month, i) => {
      cData.push({ year, month: t(month), value: totals[i], type: "Consumo" });
      aData.push({ year, month: t(month), value: avg, type: "Promedio" });
    });
  });

  return { years, consumptionData: cData, averageData: aData, yearColorMap: buildYearColorMap(years) };
}

function sumWaterDomesticSources(monthData) {
  return (
    convertUnitsFinal(monthData.water_domestic_municipal_network?.total, monthData.water_domestic_municipal_network?.unit, "l") +
    convertUnitsFinal(monthData.domestic_groundwater?.total, monthData.domestic_groundwater?.unit, "l") +
    convertUnitsFinal(monthData.rainwater?.total, monthData.rainwater?.unit, "l")
  );
}

// "Intensidad doméstica" — misma lógica que usaba el useMemo de
// WaterDomesticIntensityChart.
export function buildWaterDomesticIntensityData(resultsByYear, resultsWorkers, t) {
  const years = last3Years(resultsByYear);
  const cData = [];
  const aData = [];

  years.forEach((year) => {
    const totals = months.map((m) => {
      const monthData = resultsByYear?.[year]?.[m] || {};
      const energy = sumWaterDomesticSources(monthData);
      const production = resultsWorkers?.[year]?.[m] || 0;
      return (production > 0 && energy > 0) ? energy / production : null;
    });

    const validValues = totals.filter((v) => v !== null);
    const avg = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : null;

    months.forEach((month, i) => {
      cData.push({ year, month: t(month), value: totals[i], type: "Consumo" });
      aData.push({ year, month: t(month), value: avg, type: "Promedio" });
    });
  });

  return { years, consumptionData: cData, averageData: aData, yearColorMap: buildYearColorMap(years) };
}
